import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';

export default function SecuritySettingsPage() {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');

  const [loginSessions] = useState([
    {
      id: '1',
      device: 'MacBook Pro',
      browser: 'Chrome 120.0',
      location: 'San Francisco, CA',
      lastActive: '2025-09-28T10:30:00Z',
      current: true,
    },
    {
      id: '2',
      device: 'iPhone 15',
      browser: 'Safari Mobile',
      location: 'San Francisco, CA',
      lastActive: '2025-09-27T18:45:00Z',
      current: false,
    },
    {
      id: '3',
      device: 'iPad Air',
      browser: 'Safari Mobile',
      location: 'Los Angeles, CA',
      lastActive: '2025-09-25T14:20:00Z',
      current: false,
    },
  ]);

  const [securitySettings, setSecuritySettings] = useState({
    loginNotifications: true,
    deviceRestrictions: false,
    sessionTimeout: '24',
    dataExportNotifications: true,
    accountLockout: true,
    passwordExpiry: false,
  });

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecuritySettingChange = (field: string, value: boolean | string) => {
    setSecuritySettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangePassword = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    console.log('Changing password');
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
  };

  const handleEnable2FA = () => {
    setShowQRCode(true);
    // In real app, this would generate a QR code and backup codes
    setBackupCodes([
      '1a2b3c4d',
      '5e6f7g8h',
      '9i0j1k2l',
      '3m4n5o6p',
      '7q8r9s0t',
      '1u2v3w4x',
      '5y6z7a8b',
      '9c0d1e2f',
    ]);
  };

  const handleVerify2FA = () => {
    if (verificationCode.length === 6) {
      setTwoFactorEnabled(true);
      setShowQRCode(false);
      setVerificationCode('');
      console.log('2FA enabled successfully');
    }
  };

  const handleDisable2FA = () => {
    setTwoFactorEnabled(false);
    setBackupCodes([]);
    console.log('2FA disabled');
  };

  const handleLogoutSession = (sessionId: string) => {
    console.log('Logging out session:', sessionId);
  };

  const handleLogoutAllSessions = () => {
    console.log('Logging out all sessions');
  };

  const downloadSecurityReport = () => {
    console.log('Downloading security report');
  };

  const formatLastActive = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Settings', href: '/settings' }, { label: 'Security' }]} />

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Security Settings</h1>
            <p className="text-gray-600">Manage your account security and privacy settings</p>
          </div>
          <button
            onClick={downloadSecurityReport}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors"
          >
            Security Report
          </button>
        </div>

        <div className="space-y-6">
          {/* Password */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Password</h3>
            <div className="max-w-md space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                />
              </div>
              <button
                onClick={handleChangePassword}
                disabled={
                  !passwordData.currentPassword ||
                  !passwordData.newPassword ||
                  !passwordData.confirmPassword
                }
                className="w-full px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Change Password
              </button>
            </div>
          </div>

          {/* Two-Factor Authentication */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
                <p className="text-sm text-gray-600">
                  Add an extra layer of security to your account
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    twoFactorEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </span>
                {!twoFactorEnabled ? (
                  <button
                    onClick={handleEnable2FA}
                    className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
                  >
                    Enable 2FA
                  </button>
                ) : (
                  <button
                    onClick={handleDisable2FA}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Disable 2FA
                  </button>
                )}
              </div>
            </div>

            {showQRCode && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Scan QR Code</h4>
                    <div className="w-48 h-48 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl mb-2">📱</div>
                        <div className="text-sm text-gray-600">QR Code</div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Scan this QR code with your authenticator app
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Verify Setup</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter verification code
                        </label>
                        <input
                          type="text"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          placeholder="000000"
                          maxLength={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                        />
                      </div>
                      <button
                        onClick={handleVerify2FA}
                        disabled={verificationCode.length !== 6}
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Verify & Enable
                      </button>
                    </div>
                  </div>
                </div>

                {backupCodes.length > 0 && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Backup Codes</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Save these backup codes in a safe place. You can use them to access your
                      account if you lose your authenticator device.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-sm">
                      {backupCodes.map((code, index) => (
                        <div key={index} className="px-2 py-1 bg-white border rounded">
                          {code}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Active Sessions */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Active Sessions</h3>
              <button
                onClick={handleLogoutAllSessions}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                Log Out All
              </button>
            </div>
            <div className="space-y-3">
              {loginSessions.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      {session.device.includes('iPhone') || session.device.includes('iPad') ? (
                        <svg
                          className="w-5 h-5 text-gray-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7 2a2 2 0 00-2 2v12a2 2 0 002 2h6a2 2 0 002-2V4a2 2 0 00-2-2H7zm3 14a1 1 0 100-2 1 1 0 000 2z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5 text-gray-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {session.device}
                        {session.current && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {session.browser} • {session.location}
                      </div>
                      <div className="text-xs text-gray-500">
                        Last active: {formatLastActive(session.lastActive)}
                      </div>
                    </div>
                  </div>
                  {!session.current && (
                    <button
                      onClick={() => handleLogoutSession(session.id)}
                      className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-white/30 transition-colors"
                    >
                      Log Out
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Security Preferences */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Preferences</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Login Notifications</div>
                  <div className="text-sm text-gray-600">
                    Get notified when someone logs into your account
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securitySettings.loginNotifications}
                    onChange={(e) =>
                      handleSecuritySettingChange('loginNotifications', e.target.checked)
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Account Lockout</div>
                  <div className="text-sm text-gray-600">
                    Lock account after 5 failed login attempts
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securitySettings.accountLockout}
                    onChange={(e) =>
                      handleSecuritySettingChange('accountLockout', e.target.checked)
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Data Export Notifications</div>
                  <div className="text-sm text-gray-600">
                    Get notified when your data is exported
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securitySettings.dataExportNotifications}
                    onChange={(e) =>
                      handleSecuritySettingChange('dataExportNotifications', e.target.checked)
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">Session Timeout</div>
                  <div className="text-sm text-gray-600">
                    Automatically log out after period of inactivity
                  </div>
                </div>
                <select
                  value={securitySettings.sessionTimeout}
                  onChange={(e) => handleSecuritySettingChange('sessionTimeout', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                >
                  <option value="1">1 hour</option>
                  <option value="8">8 hours</option>
                  <option value="24">24 hours</option>
                  <option value="168">1 week</option>
                  <option value="never">Never</option>
                </select>
              </div>
            </div>
          </div>

          {/* Security Status */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="font-medium text-gray-900">Strong Password</div>
                <div className="text-sm text-gray-600">Last changed 30 days ago</div>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div
                  className={`w-12 h-12 ${twoFactorEnabled ? 'bg-green-100' : 'bg-yellow-100'} rounded-full flex items-center justify-center mx-auto mb-2`}
                >
                  <svg
                    className={`w-6 h-6 ${twoFactorEnabled ? 'text-green-600' : 'text-yellow-600'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div className="font-medium text-gray-900">Two-Factor Auth</div>
                <div className="text-sm text-gray-600">
                  {twoFactorEnabled ? 'Enabled' : 'Not enabled'}
                </div>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg
                    className="w-6 h-6 text-flowky-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div className="font-medium text-gray-900">Activity Logs</div>
                <div className="text-sm text-gray-600">{loginSessions.length} active sessions</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
